import type { CryptoSymbol } from '../../../types'
import { config } from '../../../config'
import { CRYPTO_ASSETS } from '../../../lib/constants'
import { formatMYR } from '../../../lib/utils'
import { ui, cx, text, spinner } from '../../../lib/ui-primitives'
import type { useKiosk } from '../../../hooks/useKiosk'

interface WelcomeScreenProps {
  kiosk: ReturnType<typeof useKiosk>
  onLogoPress: () => void
  onLogoRelease: () => void
  onLogoLeave: () => void
}

export function WelcomeScreen({
  kiosk,
  onLogoPress,
  onLogoRelease,
  onLogoLeave,
}: WelcomeScreenProps) {
  const { prices, pricesLoading, selectCrypto, setScreen } = kiosk

  return (
    <div className="flex flex-col h-full px-4 py-3 sm:px-6 sm:py-4">
      {/* Logo & Title - Admin access via long press */}
      <div
        className="text-center mb-4 sm:mb-5 cursor-pointer select-none shrink-0"
        onMouseDown={onLogoPress}
        onMouseUp={onLogoRelease}
        onMouseLeave={onLogoLeave}
        onTouchStart={onLogoPress}
        onTouchEnd={onLogoRelease}
        role="button"
        tabIndex={0}
        aria-label={`${config.businessName} logo - hold for admin access`}
      >
        <h1 className={cx(text.h1, 'text-xl sm:text-2xl')}>{config.businessName}</h1>
        <p className={cx('text-xs sm:text-sm', text.secondary)}>{config.businessTagline}</p>
      </div>

      {/* Crypto Selection - Scrollable if needed */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-sm mx-auto">
          <p className={cx('text-center text-xs mb-3', text.tertiary)}>
            Select cryptocurrency to buy
          </p>

          {pricesLoading ? (
            <div className="text-center py-6" role="status" aria-label="Loading prices">
              <div className={cx(spinner.medium, spinner.base, 'mx-auto mb-2')} />
              <p className={cx('text-sm', text.tertiary)}>Loading rates...</p>
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(CRYPTO_ASSETS).map(([key, asset]) => (
                <button
                  key={key}
                  onClick={() => selectCrypto(key as CryptoSymbol)}
                  className={cx(
                    'w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl border border-white/10',
                    'hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.05]',
                    'transition-colors'
                  )}
                  style={{
                    background: `linear-gradient(135deg, ${asset.color}15, rgba(255,255,255,0.02) 55%, rgba(255,255,255,0.01))`,
                  }}
                  aria-label={`Buy ${asset.name} at ${prices[key as CryptoSymbol] ? formatMYR(prices[key as CryptoSymbol]!) : 'loading'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 sm:gap-3">
                      <span className="text-xl sm:text-2xl" aria-hidden="true">
                        {asset.icon}
                      </span>
                      <div className="text-left">
                        <div className="text-white font-semibold text-sm tracking-tight">
                          {asset.symbol}
                        </div>
                        <div className={cx('text-xs', text.tertiary)}>
                          {asset.name}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={cx('text-white text-sm', text.mono, text.numeric)}>
                        {prices[key as CryptoSymbol]
                          ? formatMYR(prices[key as CryptoSymbol]!)
                          : '—'}
                      </div>
                      <div className={cx('text-[10px]', text.disabled)}>
                        {asset.networks.length} network{asset.networks.length > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer - Track Order */}
      <div className="pt-3 shrink-0">
        <button
          onClick={() => setScreen('lookup')}
          className={cx(ui.btnBase, ui.btnGhost, 'w-full py-2.5 text-xs sm:text-sm')}
        >
          <span aria-hidden="true">🔍</span> Track Existing Order
        </button>
      </div>
    </div>
  )
}
